/**
 * AdminBroadcastTab.js
 * 
 * Admin interface for sending broadcast messages to:
 * - All users
 * - Users by tier (Inner Circle, Inner Edge, SDC, etc.)
 * - All coaches
 * 
 * Supports email and SMS channels.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Send, 
  Users, 
  Mail, 
  MessageSquare, 
  Loader2,
  Clock,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  History,
  Calendar,
  X,
  Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBroadcastTab = () => {
  // Form state
  const [recipientType, setRecipientType] = useState('all_users');
  const [tierFilter, setTierFilter] = useState('');
  const [channel, setChannel] = useState('email');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  
  // UI state
  const [sending, setSending] = useState(false);
  const [recipientCounts, setRecipientCounts] = useState(null);
  const [broadcastHistory, setBroadcastHistory] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  
  // Fetch recipient counts on mount
  useEffect(() => {
    fetchRecipientCounts();
  }, []);
  
  const fetchRecipientCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/broadcast/recipient-counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipientCounts(response.data);
    } catch (error) {
      console.error('Failed to fetch recipient counts:', error);
    }
  };
  
  const fetchBroadcastHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/broadcast/history?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBroadcastHistory(response.data.broadcasts || []);
    } catch (error) {
      console.error('Failed to fetch broadcast history:', error);
      toast.error('Failed to load broadcast history');
    } finally {
      setLoadingHistory(false);
    }
  };
  
  const getRecipientCount = () => {
    if (!recipientCounts) return 0;
    
    if (recipientType === 'all_users') {
      return recipientCounts.all_users || 0;
    } else if (recipientType === 'all_coaches') {
      return recipientCounts.all_coaches || 0;
    } else if (recipientType === 'by_tier' && tierFilter) {
      return recipientCounts.by_tier?.[tierFilter]?.count || 0;
    }
    return 0;
  };
  
  const getRecipientLabel = () => {
    if (recipientType === 'all_users') return 'All Active Users';
    if (recipientType === 'all_coaches') return 'All Coaches';
    if (recipientType === 'by_tier' && tierFilter && recipientCounts?.by_tier?.[tierFilter]) {
      return recipientCounts.by_tier[tierFilter].display_name + ' Members';
    }
    return 'Select Recipients';
  };
  
  const handleSendBroadcast = async () => {
    setShowConfirmDialog(false);
    setSending(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Build request payload
      const payload = {
        recipient_type: recipientType,
        tier_filter: recipientType === 'by_tier' ? tierFilter : null,
        channel,
        subject,
        message,
        cta_text: ctaText || null,
        cta_url: ctaUrl || null
      };
      
      // Add scheduled_at if scheduling
      if (isScheduled && scheduledDate && scheduledTime) {
        const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
        payload.scheduled_at = scheduledDateTime.toISOString();
      }
      
      const response = await axios.post(
        `${API_URL}/api/admin/broadcast/send`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      
      if (result.status === 'scheduled') {
        toast.success(
          `Broadcast scheduled for ${new Date(result.scheduled_at).toLocaleString()}! Will be sent to ${result.estimated_recipients} recipients.`
        );
      } else if (result.status === 'completed') {
        toast.success(
          `Broadcast sent! ${result.emails_sent} emails, ${result.sms_sent} SMS delivered.`
        );
      }
      
      // Clear form
      setSubject('');
      setMessage('');
      setCtaText('');
      setCtaUrl('');
      setIsScheduled(false);
      setScheduledDate('');
      setScheduledTime('');
    } catch (error) {
      console.error('Broadcast failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };
  
  const handleCancelBroadcast = async (broadcastId) => {
    setCancellingId(broadcastId);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/admin/broadcast/${broadcastId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Scheduled broadcast cancelled');
      fetchBroadcastHistory();
    } catch (error) {
      console.error('Cancel failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel broadcast');
    } finally {
      setCancellingId(null);
    }
  };
  
  const validateForm = () => {
    if (recipientType === 'by_tier' && !tierFilter) {
      toast.error('Please select a tier');
      return false;
    }
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return false;
    }
    if (!message.trim()) {
      toast.error('Please enter a message');
      return false;
    }
    if (ctaText && !ctaUrl) {
      toast.error('Please enter a URL for the button');
      return false;
    }
    if (isScheduled) {
      if (!scheduledDate || !scheduledTime) {
        toast.error('Please select a date and time for scheduling');
        return false;
      }
      const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      if (scheduledDateTime <= new Date()) {
        toast.error('Scheduled time must be in the future');
        return false;
      }
    }
    return true;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setShowConfirmDialog(true);
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-forest">Broadcast Messages</h2>
          <p className="text-gray-600 mt-1">Send messages to users, members by tier, or coaches</p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            fetchBroadcastHistory();
            setShowHistoryDialog(true);
          }}
          data-testid="broadcast-history-btn"
        >
          <History className="h-4 w-4 mr-2" />
          History
        </Button>
      </div>
      
      {/* Recipient Stats */}
      {recipientCounts && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-forest/5 border-forest/20">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-forest">{recipientCounts.all_users}</div>
              <div className="text-sm text-gray-600">Active Users</div>
            </CardContent>
          </Card>
          <Card className="bg-terracotta/5 border-terracotta/20">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-terracotta">{recipientCounts.all_coaches}</div>
              <div className="text-sm text-gray-600">Coaches</div>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-purple-600">
                {recipientCounts.by_tier?.inner_circle?.count || 0}
              </div>
              <div className="text-sm text-gray-600">Inner Circle</div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">
                {recipientCounts.by_tier?.inner_edge?.count || 0}
              </div>
              <div className="text-sm text-gray-600">Inner Edge</div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Broadcast Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-forest" />
            Compose Broadcast
          </CardTitle>
          <CardDescription>
            Messages will be sent immediately to all selected recipients
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Recipients */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipients
                </label>
                <Select value={recipientType} onValueChange={setRecipientType}>
                  <SelectTrigger data-testid="recipient-type-select">
                    <SelectValue placeholder="Select recipients" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_users">
                      All Active Users ({recipientCounts?.all_users || 0})
                    </SelectItem>
                    <SelectItem value="all_coaches">
                      All Coaches ({recipientCounts?.all_coaches || 0})
                    </SelectItem>
                    <SelectItem value="by_tier">By Tier...</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Tier Filter */}
              {recipientType === 'by_tier' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Tier
                  </label>
                  <Select value={tierFilter} onValueChange={setTierFilter}>
                    <SelectTrigger data-testid="tier-filter-select">
                      <SelectValue placeholder="Select tier" />
                    </SelectTrigger>
                    <SelectContent>
                      {recipientCounts?.by_tier && Object.entries(recipientCounts.by_tier).map(([key, value]) => (
                        <SelectItem key={key} value={key}>
                          {value.display_name} ({value.count})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Channel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Channel
                </label>
                <Select value={channel} onValueChange={setChannel}>
                  <SelectTrigger data-testid="channel-select">
                    <SelectValue placeholder="Select channel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">
                      <span className="flex items-center gap-2">
                        <Mail className="h-4 w-4" /> Email Only
                      </span>
                    </SelectItem>
                    <SelectItem value="sms">
                      <span className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" /> SMS Only
                      </span>
                    </SelectItem>
                    <SelectItem value="both">
                      <span className="flex items-center gap-2">
                        <Users className="h-4 w-4" /> Email + SMS
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject Line
              </label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject..."
                maxLength={100}
                data-testid="broadcast-subject"
              />
            </div>
            
            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter your message... (Use double line breaks for paragraphs)"
                rows={6}
                className="resize-none"
                data-testid="broadcast-message"
              />
              <p className="text-xs text-gray-500 mt-1">
                {message.length} characters {channel !== 'email' && '(SMS will be truncated to 160 chars)'}
              </p>
            </div>
            
            {/* CTA Button (Optional) */}
            {channel !== 'sms' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Button Text (Optional)
                  </label>
                  <Input
                    value={ctaText}
                    onChange={(e) => setCtaText(e.target.value)}
                    placeholder="e.g., Learn More"
                    data-testid="broadcast-cta-text"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Button URL
                  </label>
                  <Input
                    value={ctaUrl}
                    onChange={(e) => setCtaUrl(e.target.value)}
                    placeholder="https://sisterdiamonds.com/..."
                    data-testid="broadcast-cta-url"
                  />
                </div>
              </div>
            )}
            
            {/* Scheduling Option */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-4">
                <input
                  type="checkbox"
                  id="schedule-broadcast"
                  checked={isScheduled}
                  onChange={(e) => setIsScheduled(e.target.checked)}
                  className="rounded border-gray-300 text-forest focus:ring-forest"
                  data-testid="schedule-checkbox"
                />
                <label htmlFor="schedule-broadcast" className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                  <Calendar className="h-4 w-4 text-forest" />
                  Schedule for Later
                </label>
              </div>
              
              {isScheduled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-7">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date
                    </label>
                    <Input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      data-testid="scheduled-date"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Time
                    </label>
                    <Input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      data-testid="scheduled-time"
                    />
                  </div>
                  {scheduledDate && scheduledTime && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-forest">
                        <Clock className="h-4 w-4 inline mr-1" />
                        Will be sent on {new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Preview */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Preview</h4>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Badge variant="outline" className="bg-white">
                  {getRecipientLabel()}
                </Badge>
                <span>•</span>
                <Badge variant="outline" className="bg-white">
                  {getRecipientCount()} recipients
                </Badge>
                <span>•</span>
                <Badge variant="outline" className="bg-white">
                  {channel === 'email' ? 'Email' : channel === 'sms' ? 'SMS' : 'Email + SMS'}
                </Badge>
              </div>
            </div>
            
            {/* Submit */}
            <div className="flex justify-end gap-3">
              {isScheduled && scheduledDate && scheduledTime && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsScheduled(false);
                    setScheduledDate('');
                    setScheduledTime('');
                  }}
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear Schedule
                </Button>
              )}
              <Button
                type="submit"
                disabled={sending}
                className="bg-forest hover:bg-forest/90"
                data-testid="send-broadcast-btn"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {isScheduled ? 'Scheduling...' : 'Sending...'}
                  </>
                ) : isScheduled ? (
                  <>
                    <Calendar className="h-4 w-4 mr-2" />
                    Schedule Broadcast
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Now
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      
      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isScheduled ? (
                <Calendar className="h-5 w-5 text-forest" />
              ) : (
                <AlertCircle className="h-5 w-5 text-terracotta" />
              )}
              {isScheduled ? 'Confirm Schedule' : 'Confirm Broadcast'}
            </DialogTitle>
            <DialogDescription>
              {isScheduled ? (
                <>You are about to schedule a message to <strong>{getRecipientCount()}</strong> recipients.</>
              ) : (
                <>You are about to send a message to <strong>{getRecipientCount()}</strong> recipients. This action cannot be undone.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <p><strong>Recipients:</strong> {getRecipientLabel()}</p>
            <p><strong>Channel:</strong> {channel === 'both' ? 'Email + SMS' : channel.toUpperCase()}</p>
            <p><strong>Subject:</strong> {subject}</p>
            {isScheduled && scheduledDate && scheduledTime && (
              <p className="text-forest">
                <strong>Scheduled for:</strong> {new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString()}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendBroadcast}
              className="bg-forest hover:bg-forest/90"
            >
              {isScheduled ? (
                <>
                  <Calendar className="h-4 w-4 mr-2" />
                  Schedule
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Now
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-forest" />
              Broadcast History
            </DialogTitle>
          </DialogHeader>
          {loadingHistory ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-forest" />
            </div>
          ) : broadcastHistory.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No broadcasts sent yet</p>
          ) : (
            <div className="space-y-4">
              {broadcastHistory.map((broadcast) => (
                <Card key={broadcast.id} className={`${broadcast.status === 'scheduled' ? 'bg-blue-50 border-blue-200' : broadcast.status === 'cancelled' ? 'bg-gray-100 border-gray-300' : 'bg-gray-50'}`}>
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className={`font-medium ${broadcast.status === 'cancelled' ? 'text-gray-400 line-through' : ''}`}>{broadcast.subject}</h4>
                        <p className="text-sm text-gray-500 mt-1">
                          {broadcast.recipient_type === 'all_users' ? 'All Users' :
                           broadcast.recipient_type === 'all_coaches' ? 'All Coaches' :
                           `Tier: ${broadcast.tier_filter}`}
                          {' • '}
                          {broadcast.channel}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          broadcast.status === 'completed' ? 'bg-green-100 text-green-700' : 
                          broadcast.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                          broadcast.status === 'cancelled' ? 'bg-gray-200 text-gray-600' :
                          broadcast.status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }>
                          {broadcast.status === 'completed' ? (
                            <><CheckCircle2 className="h-3 w-3 mr-1" /> Sent</>
                          ) : broadcast.status === 'scheduled' ? (
                            <><Calendar className="h-3 w-3 mr-1" /> Scheduled</>
                          ) : broadcast.status === 'cancelled' ? (
                            <><X className="h-3 w-3 mr-1" /> Cancelled</>
                          ) : broadcast.status === 'failed' ? (
                            <><AlertCircle className="h-3 w-3 mr-1" /> Failed</>
                          ) : (
                            <><Clock className="h-3 w-3 mr-1" /> {broadcast.status}</>
                          )}
                        </Badge>
                        {broadcast.status === 'scheduled' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleCancelBroadcast(broadcast.id)}
                            disabled={cancellingId === broadcast.id}
                            data-testid={`cancel-broadcast-${broadcast.id}`}
                          >
                            {cancellingId === broadcast.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Show scheduled time for scheduled broadcasts */}
                    {broadcast.status === 'scheduled' && broadcast.scheduled_at && (
                      <div className="mt-2 text-sm text-blue-600 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Scheduled for: {new Date(broadcast.scheduled_at).toLocaleString()}
                        <span className="text-gray-400 ml-2">
                          ({broadcast.estimated_recipients || 0} estimated recipients)
                        </span>
                      </div>
                    )}
                    
                    {/* Show results for completed broadcasts */}
                    {broadcast.status === 'completed' && broadcast.results && (
                      <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                        <div>
                          <span className="text-gray-500">Recipients:</span>{' '}
                          <span className="font-medium">{broadcast.results?.total_recipients || 0}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Emails:</span>{' '}
                          <span className="font-medium text-green-600">{broadcast.results?.emails_sent || 0}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">SMS:</span>{' '}
                          <span className="font-medium text-blue-600">{broadcast.results?.sms_sent || 0}</span>
                        </div>
                      </div>
                    )}
                    
                    <p className="text-xs text-gray-400 mt-2">
                      Created: {new Date(broadcast.created_at).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBroadcastTab;
